import {
  addMessage as addMessageAction,
  applyStreamEvent,
  clearMemories as clearMemoriesAction,
  clearMessages,
  removeMemory as removeMemoryAction,
  removeSavedPrompt as removeSavedPromptAction,
  setActiveConversationWithMessages as setActiveConversationWithMessagesAction,
  setConfirmResolution as setConfirmResolutionAction,
  toggleSavedPrompt as toggleSavedPromptAction,
  setMemories as setMemoriesAction,
  setMessages as setMessagesAction,
  setSavedPrompts as setSavedPromptsAction,
} from "@redux/slices/askDostChat";
import { useDispatch, useSelector } from "@redux/store";
import { useSnackbar } from "notistack";
import { useCallback, useState } from "react";
import { UseFormReset } from "react-hook-form";
import {
  deleteSavedPrompt,
  forgetEverything,
  forgetPattern,
  getMemories,
  getSavedPrompts,
  resetChat,
  savePrompt,
} from "../services/askDostService";
import {
  IChatMessage,
  IConfirmRequest,
  ISendChatMessage,
  ISSEEvent,
} from "../types";
import { is } from "date-fns/locale";

interface IUseChatHistory {
  translate: (text: string, options?: any) => string;
  reset?: UseFormReset<ISendChatMessage>;
}

export const useChatHistory = ({ translate, reset }: IUseChatHistory) => {
  const dispatch = useDispatch();
  const messages = useSelector((state: any) => state.askDostChat.messages);
  const savedPrompts = useSelector(
    (state: any) => state.askDostChat.savedPrompts,
  );
  const [isResetting, setIsResetting] = useState(false);
  const memories = useSelector((state: any) => state.askDostChat.memories);
  const { enqueueSnackbar } = useSnackbar();

  const fetchSavedPromptsList = useCallback(async () => {
    try {
      const res = await getSavedPrompts();
      if (res && res.status === 200) {
        const rawPrompts = res.data?.prompts || res.data || [];
        const formattedData = rawPrompts.map((item: any) => ({
          id: item.id,
          text: item.text || item.prompt,
        }));

        dispatch(setSavedPromptsAction(formattedData));
      }
    } catch (error) {
      console.error("Failed to fetch saved prompts:", error);
    }
  }, [dispatch]);

  const fetchMemories = useCallback(async () => {
    try {
      const res = await getMemories();
      if (res && res.status === 200) {
        const rawPatterns = res.data?.patterns || [];
        const formattedData = rawPatterns.map((item: any, index: number) => ({
          id: item.id || `mem-${index}`,
          text: item.statement || translate("no_statement_available"),
          when: item.last_seen
            ? translate("last_seen", { date: item.last_seen })
            : "",
        }));

        dispatch(setMemoriesAction(formattedData));
      }
    } catch (error) {
      console.error("Failed to fetch memories:", error);
    }
  }, [dispatch]);

  const clearHistory = useCallback(async () => {
    setIsResetting(true);
    resetChat().then((res) => {
      if (res.status === 200) {
        dispatch(clearMessages());
        reset?.();
        setIsResetting(false);
      } else {
        enqueueSnackbar(translate("unable_to_clear_chat"), {
          variant: "error",
        });
        setIsResetting(false);
      }
    });
  }, [dispatch, enqueueSnackbar, reset, translate]);

  const addMessage = useCallback(
    (message: IChatMessage) => {
      dispatch(addMessageAction(message));
    },
    [dispatch],
  );

  const setMessages = useCallback(
    (newMessages: IChatMessage[]) => {
      dispatch(setMessagesAction(newMessages));
    },
    [dispatch],
  );

  const setActiveConversationWithMessages = useCallback(
    (id: string | null, title: string | null, messages: IChatMessage[]) => {
      dispatch(
        setActiveConversationWithMessagesAction({ id, title, messages }),
      );
    },
    [dispatch],
  );

  const handleStreamEvent = useCallback(
    (event: ISSEEvent) => {
      dispatch(applyStreamEvent(event));
    },
    [dispatch],
  );

  const setConfirmResolution = useCallback(
    (blockId: string, resolution: IConfirmRequest, writeId?: string) => {
      dispatch(setConfirmResolutionAction({ blockId, resolution, writeId }));
    },
    [dispatch],
  );

  const toggleSavedPrompt = useCallback(
    async (text: string) => {
      try {
        const res = await savePrompt({ text });
        if (res && (res.status === 200 || res.status === 201)) {
          fetchSavedPromptsList();
          enqueueSnackbar(translate("prompt_saved"), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(translate("unable_to_save_prompt"), {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Error saving prompt:", error);
        enqueueSnackbar(translate("unable_to_save_prompt"), {
          variant: "error",
        });
      }
    },
    [enqueueSnackbar, translate, fetchSavedPromptsList],
  );

  const removeSavedPrompt = useCallback(
    async (id: string) => {
      try {
        const res = await deleteSavedPrompt(id);
        if (res && (res.status === 200 || res.status === 204)) {
          dispatch(removeSavedPromptAction(id));
          enqueueSnackbar(translate("prompt_deleted"), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(translate("unable_to_delete_prompt"), {
            variant: "error",
          });
        }
      } catch (error) {
        console.error("Error deleting prompt:", error);
        enqueueSnackbar(translate("unable_to_delete_prompt"), {
          variant: "error",
        });
      }
    },
    [dispatch, enqueueSnackbar, translate],
  );

  const removeMemory = useCallback(
    async (id: string) => {
      try {
        const res = await forgetPattern(id);
        if (res && (res.status === 200 || res.status === 204)) {
          dispatch(removeMemoryAction(id));
          enqueueSnackbar(translate("memory_deleted"), {
            variant: "success",
          });
        } else {
          enqueueSnackbar(translate("unable_to_delete_memory"), {
            variant: "error",
          });
        }
      } catch (error) {
        enqueueSnackbar(translate("unable_to_delete_memory"), {
          variant: "error",
        });
      }
    },
    [dispatch, enqueueSnackbar, translate],
  );

  const clearMemoriesList = useCallback(async () => {
    try {
      const res = await forgetEverything();
      if (res && (res.status === 200 || res.status === 204)) {
        dispatch(clearMemoriesAction());
        enqueueSnackbar(translate("memories_cleared"), { variant: "success" });
      } else {
        enqueueSnackbar(translate("unable_to_clear_memories"), {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(translate("unable_to_clear_memories"), {
        variant: "error",
      });
    }
  }, [dispatch, enqueueSnackbar, translate]);

  return {
    messages,
    addMessage,
    setMessages,
    isResetting,
    setActiveConversationWithMessages,
    clearHistory,
    handleStreamEvent,
    setConfirmResolution,
    savedPrompts,
    toggleSavedPrompt,
    removeSavedPrompt,
    memories,
    removeMemory,
    clearMemories: clearMemoriesList,
    fetchMemories,
    fetchSavedPrompts: fetchSavedPromptsList,
  };
};
